import { buildOperationsPlan, buildSetupBody, mergeOperationResults, operationsToRetry } from "./operationsPlan";

export function fail(code, message, extra = {}) {
  const error = new Error(message);
  error.code = code;
  Object.assign(error, extra);
  return error;
}

function normEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function normPhone(value) {
  return String(value || "").replace(/[^\d+]/g, "");
}

// Map a draft contact (no ID yet) to exactly one target contact by its
// email/phone destination; ambiguous or missing matches stay unresolved and
// the server performs the authoritative resolution.
export function resolveTargetContactId(row, recipients, addressTypes) {
  if (row.contact_id) return Number(row.contact_id);
  const email = normEmail(row.email);
  const phone = normPhone(row.phone);
  const matches = (recipients || []).filter((recipient) =>
    recipient.scope === "entity" &&
    (recipient.addresses || []).some((address) => {
      if (email && Number(address.address_type_id) === Number(addressTypes?.email)) return normEmail(address.value) === email;
      if (phone && Number(address.address_type_id) === Number(addressTypes?.phone)) return normPhone(address.value) === phone;
      return false;
    }),
  );
  return matches.length === 1 ? Number(matches[0].contact_id) : null;
}

const PROGRESS_PREFIX = "rivio_claim_install_progress:";

export function readProgress(claimCode) {
  try {
    const raw = sessionStorage.getItem(`${PROGRESS_PREFIX}${claimCode}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Holds canonical IDs and the frozen plan only: never session credentials.
export function writeProgress(claimCode, progress) {
  try {
    sessionStorage.setItem(`${PROGRESS_PREFIX}${claimCode}`, JSON.stringify(progress));
  } catch {
    /* storage unavailable: the server summary still carries the plan */
  }
}

export function clearProgress(claimCode) {
  try {
    sessionStorage.removeItem(`${PROGRESS_PREFIX}${claimCode}`);
  } catch {
    /* storage unavailable */
  }
}

// Tell claims tables in other tabs to revalidate.
export function announceClaimChange(claimId) {
  try {
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel("rivio-device-claims");
      channel.postMessage({ type: "claim-changed", claimId, at: Date.now() });
      channel.close();
    }
    localStorage.setItem("rivio_device_claims_changed", String(Date.now()));
  } catch {
    /* best effort; the claims page also revalidates on focus and polls */
  }
}

// True only when the deployed backend advertises the claim-scoped
// level_calibration operation. Until then calibration must not be added to
// the plan: today's strict setup schema rejects unknown operation kinds.
export function supportsCalibrationOperation(options) {
  return options?.capabilities?.level_calibration_operation === true;
}

/**
 * Core install for a reviewed, already-saved target. Runs, in order:
 * target-scoped setup options -> frozen operations plan -> beforeComplete
 * hook -> setup save -> the single completion request (with status recovery
 * on an ambiguous error).
 * Returns { completed, plan, options, calibrationViaOperation }.
 *
 * intents.levelCalibration (reviewed four-field payload or null) is frozen
 * into the plan only when the server supports the operation. beforeComplete
 * receives { options, calibrationViaOperation } so the caller can choose the
 * single calibration save path before anything is completed; if it throws,
 * nothing is saved or completed.
 */
export async function completeReviewedInstallation({ api, session, target, install, intents, onStage, beforeComplete }) {
  const { entityId, addressId, assetId } = target;
  if (!entityId || !addressId || !assetId) {
    throw fail("TARGET_INCOMPLETE", "Select the saved customer, service address, and asset before installing.");
  }
  onStage?.("submitting");
  const options = await api.claimSetupOptions(session, { entityId, assetId });
  const lookups = options?.lookups || {};
  if (!lookups.terms_version) {
    throw fail("INSTALLATION_TERMS_NOT_CONFIGURED", "Installation terms are not configured. Contact support before installing.");
  }
  const asset = (options.assets || []).find((row) => Number(row.address_item_id) === Number(assetId));
  if (!asset || Number(asset.entity_id) !== Number(entityId) || Number(asset.address_id) !== Number(addressId)) {
    throw fail("ASSET_NOT_IN_TARGET", "The selected asset does not belong to the saved customer and service address.");
  }
  const contactAccess = (intents.contactAccess || []).map((row) => ({
    ...row,
    contact_id: resolveTargetContactId(row, options.recipients, lookups.address_types),
  }));
  const recipients = (intents.alerts?.recipients || []).map((row) => ({
    ...row,
    contact_id: row.scope === "entity" ? resolveTargetContactId(
      { contact_id: row.contact_id, email: row.kind === "email" ? row.value : null, phone: row.kind === "sms" ? row.value : null },
      options.recipients,
      lookups.address_types,
    ) : row.contact_id,
  }));
  const calibrationViaOperation = Boolean(intents.levelCalibration) && supportsCalibrationOperation(options);
  const { operations, errors } = buildOperationsPlan({
    claimId: session.claim_id,
    assetId,
    providerIds: intents.providerIds || [],
    contactAccess,
    alerts: { ...(intents.alerts || {}), recipients },
    thresholds: install.thresholds || [],
    lookups,
    levelCalibration: calibrationViaOperation ? intents.levelCalibration : null,
  });
  if (errors.length) throw fail("SETUP_PLAN_INVALID", errors.join(" "));
  await beforeComplete?.({ options, calibrationViaOperation });
  await api.saveSetup(
    session,
    buildSetupBody({
      entityId,
      assetId,
      providerIds: intents.providerIds || [],
      contactAccess,
      notificationRules: operations.filter((row) => row.kind === "notification_rule"),
      operations,
    }),
  );
  const payload = {
    attest_data_accuracy: true,
    accept_terms: true,
    terms_version: lookups.terms_version,
    installation_date: install.installationDate,
    target_entity_id: Number(entityId),
    target_address_id: Number(addressId),
    entity_address_item_id: Number(assetId),
    device_name: String(install.deviceName || "").trim(),
    asset: install.asset,
    thresholds: install.thresholds || [],
    ...(install.billingContactId ? { billing_contact_id: Number(install.billingContactId) } : {}),
  };
  let completed;
  try {
    completed = await api.complete(session, payload);
  } catch (error) {
    // Recover an ambiguous response before offering another submit.
    let status = null;
    try {
      status = await api.status(session.claim_id);
    } catch {
      status = null;
    }
    if (status?.status === "completed") {
      completed = { ...status, recovered: true };
    } else {
      throw fail(error?.code || "ASSIGNMENT_FAILED", error?.message || "Device assignment failed.", { assignment: true });
    }
  }
  announceClaimChange(session.claim_id);
  return { completed, plan: operations, options, calibrationViaOperation };
}

// Apply (or retry) claim-scoped follow-ups, then reload the historical summary.
export async function applyFollowUps({ api, claimId, operations }) {
  let results = [];
  let applyError = null;
  const pending = operationsToRetry(operations);
  if (pending.length) {
    try {
      const response = await api.applyOperations(claimId, pending);
      results = response?.operations || [];
    } catch (error) {
      applyError = error;
    }
  }
  let summary = null;
  try {
    summary = await api.summary(claimId);
  } catch (error) {
    if (!applyError) applyError = error;
  }
  const merged = summary?.operations?.length
    ? summary.operations
    : mergeOperationResults(operations, results);
  announceClaimChange(claimId);
  return { summary, operations: merged, error: applyError };
}
